import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { useSitzung } from '../sitzung';
import { Eingabe, Feld, Knopf, Meldung, useAktion } from '../ui';

function Rahmen({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="zugang">
      <div className="zugang-karte">
        <div className="zugang-logo">
          <img src="/beholder.svg" alt="" width={48} height={48} />
          <span>Beholder</span>
        </div>
        <h1>{titel}</h1>
        {children}
      </div>
    </div>
  );
}

export function Anmelden() {
  const { neuLaden } = useSitzung();
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const a = useAktion();
  async function senden(e: FormEvent) {
    e.preventDefault();
    const ok = await a.ausfuehren(() => api.post('/api/anmelden', { benutzername: name, passwort: pw }));
    if (ok) await neuLaden();
  }
  return (
    <Rahmen titel="Anmelden">
      <form onSubmit={senden} className="stapel">
        <Feld titel="Benutzername">
          <Eingabe autoFocus autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} />
        </Feld>
        <Feld titel="Passwort">
          <Eingabe type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </Feld>
        <Meldung>{a.fehler}</Meldung>
        <Knopf art="haupt" type="submit" disabled={a.laeuft || !name || !pw}>
          {a.laeuft ? 'Prüfe …' : 'Anmelden'}
        </Knopf>
      </form>
    </Rahmen>
  );
}

export function Einrichten() {
  const { neuLaden } = useSitzung();
  const [f, setF] = useState({ code: '', benutzername: '', anzeigename: '', passwort: '', wiederholung: '' });
  const a = useAktion();
  const setze = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  async function senden(e: FormEvent) {
    e.preventDefault();
    if (f.passwort !== f.wiederholung) return a.setFehler('Die Passwörter stimmen nicht überein');
    const ok = await a.ausfuehren(() => api.post('/api/einrichtung', f));
    if (ok) await neuLaden();
  }
  return (
    <Rahmen titel="Ersten Admin anlegen">
      <p className="leise">
        Beholder ist noch leer. Der Einrichtungscode steht in der <code>.env</code> auf dem Server
        (<code>BEHOLDER_EINRICHTUNGSCODE</code>) oder im Server-Protokoll.
      </p>
      <form onSubmit={senden} className="stapel">
        <Feld titel="Einrichtungscode">
          <Eingabe autoFocus value={f.code} onChange={setze('code')} />
        </Feld>
        <Feld titel="Benutzername" hinweis="3–32 Zeichen: a–z, 0–9, Punkt, Strich">
          <Eingabe autoComplete="username" value={f.benutzername} onChange={setze('benutzername')} />
        </Feld>
        <Feld titel="Anzeigename">
          <Eingabe value={f.anzeigename} onChange={setze('anzeigename')} />
        </Feld>
        <Feld titel="Passwort" hinweis="Mindestens 10 Zeichen">
          <Eingabe type="password" autoComplete="new-password" value={f.passwort} onChange={setze('passwort')} />
        </Feld>
        <Feld titel="Passwort wiederholen">
          <Eingabe type="password" autoComplete="new-password" value={f.wiederholung} onChange={setze('wiederholung')} />
        </Feld>
        <Meldung>{a.fehler}</Meldung>
        <Knopf art="haupt" type="submit" disabled={a.laeuft}>
          Einrichten
        </Knopf>
      </form>
    </Rahmen>
  );
}

export function PasswortFormular({ fertig, pflicht }: { fertig(): void; pflicht?: boolean }) {
  const [f, setF] = useState({ alt: '', neu: '', wiederholung: '' });
  const a = useAktion();
  const setze = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  async function senden(e: FormEvent) {
    e.preventDefault();
    if (f.neu !== f.wiederholung) return a.setFehler('Die neuen Passwörter stimmen nicht überein');
    const ok = await a.ausfuehren(() => api.post('/api/ich/passwort', { alt: f.alt, neu: f.neu }));
    if (ok) {
      setF({ alt: '', neu: '', wiederholung: '' });
      fertig();
    }
  }
  return (
    <form onSubmit={senden} className="stapel">
      <Feld titel={pflicht ? 'Startpasswort' : 'Aktuelles Passwort'}>
        <Eingabe type="password" autoComplete="current-password" value={f.alt} onChange={setze('alt')} />
      </Feld>
      <Feld titel="Neues Passwort" hinweis="Mindestens 10 Zeichen">
        <Eingabe type="password" autoComplete="new-password" value={f.neu} onChange={setze('neu')} />
      </Feld>
      <Feld titel="Neues Passwort wiederholen">
        <Eingabe type="password" autoComplete="new-password" value={f.wiederholung} onChange={setze('wiederholung')} />
      </Feld>
      <Meldung>{a.fehler}</Meldung>
      <Knopf art="haupt" type="submit" disabled={a.laeuft || !f.alt || !f.neu}>
        Passwort ändern
      </Knopf>
    </form>
  );
}

export function PasswortPflicht() {
  const { neuLaden, abmelden } = useSitzung();
  return (
    <Rahmen titel="Neues Passwort festlegen">
      <p className="leise">Du hast ein Startpasswort bekommen. Bitte lege jetzt dein eigenes fest.</p>
      <PasswortFormular pflicht fertig={() => void neuLaden()} />
      <Knopf art="leise" onClick={() => void abmelden()}>
        Abmelden
      </Knopf>
    </Rahmen>
  );
}
